import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Typography, 
  Row, 
  Col, 
  Divider,
  Table,
  Tag
} from 'antd';
import { } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const { Title } = Typography;

interface Payment {
  _id: string;
  amount: number;
  subscriptionPlan: string;
  planDuration: number;
  status: string;
  paymentDate: string;
  expiryDate: string;
}

const Profile: React.FC = () => {
  const { stationOwner } = useAuth();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    if (stationOwner) {
      form.setFieldsValue({
        firstName: stationOwner.firstName,
        lastName: stationOwner.lastName,
        email: stationOwner.email,
        companyName: stationOwner.companyName,
      });
    }
    fetchPayments();
  }, [stationOwner, form]);

  const fetchPayments = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://web-production-bdd98.up.railway.app/api';
      const response = await axios.get(`${API_BASE_URL}/station-owners/payments`);
      setPayments(response.data);
    } catch (error) {
      message.error('Ödeme geçmişi yüklenemedi');
    }
  };

  const handleUpdateProfile = async (values: any) => {
    setLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://web-production-bdd98.up.railway.app/api';
      await axios.put(`${API_BASE_URL}/station-owners/profile`, values);
      message.success('Profil güncellendi');
    } catch (error) {
      message.error('Profil güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'green';
      case 'pending': return 'orange';
      case 'failed': return 'red';
      default: return 'blue';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Tamamlandı';
      case 'pending': return 'Beklemede';
      case 'failed': return 'Başarısız';
      default: return status;
    }
  };

  const paymentColumns = [
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `${amount}₺`,
    },
    {
      title: 'Plan',
      dataIndex: 'subscriptionPlan',
      key: 'subscriptionPlan',
      render: (plan: string) => (
        <Tag color={plan === 'premium' ? 'gold' : 'blue'}>
          {plan === 'premium' ? 'Premium' : 'Temel'}
        </Tag>
      ),
    },
    {
      title: 'Süre',
      dataIndex: 'planDuration',
      key: 'planDuration',
      render: (duration: number) => `${duration} ay`,
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Ödeme Tarihi',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR'),
    },
    {
      title: 'Son Kullanma',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR'),
    },
  ];

  return (
    <div>
      <Title level={2} style={{ marginBottom: '24px' }}>
        Profil Bilgileri
      </Title>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title="Kişisel Bilgiler">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleUpdateProfile}
            >
              <Form.Item
                name="firstName"
                label="Ad"
                rules={[{ required: true, message: 'Ad gerekli' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="lastName"
                label="Soyad"
                rules={[{ required: true, message: 'Soyad gerekli' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="email"
                label="E-posta"
                rules={[
                  { required: true, message: 'E-posta gerekli' },
                  { type: 'email', message: 'Geçerli e-posta girin' }
                ]}
              >
                <Input disabled />
              </Form.Item>

              <Form.Item
                name="companyName"
                label="Şirket Adı"
                rules={[{ required: true, message: 'Şirket adı gerekli' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Güncelle
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Abonelik Bilgileri">
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{ 
                fontSize: '24px', 
                fontWeight: 'bold',
                color: stationOwner?.subscriptionStatus === 'premium' ? '#FF6B35' : '#52c41a',
                marginBottom: '8px'
              }}>
                {stationOwner?.subscriptionStatus === 'premium' ? 'Premium Üyelik' : 'Temel Üyelik'}
              </div>
              {stationOwner?.subscriptionExpiry && (
                <div style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
                  Son kullanma: {new Date(stationOwner.subscriptionExpiry).toLocaleDateString('tr-TR')}
                </div>
              )}
              <Button 
                type="primary" 
                size="large"
                style={{ 
                  background: '#FF6B35',
                  borderColor: '#FF6B35'
                }}
              >
                Premium'a Yükselt
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      <Divider />

      <Card title="Ödeme Geçmişi">
        <Table
          columns={paymentColumns}
          dataSource={payments}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} ödeme`,
          }}
        />
      </Card>
    </div>
  );
};

export default Profile;
